// 壳层纯窗口状态 store。外链 APP（externalUrl）会在 launchApp 中
// 通过 window.open 打开，不会进入这里；这样这个 store 保持无副作用，
// 也更容易做单元测试。见 design.md §4.4。
import type { ComponentType } from 'react';
import { create } from 'zustand';
import type { IconSource } from '@blade/os-sdk';

export interface WindowGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WindowState extends WindowGeometry {
  id: string;
  appId: string;
  title: string;
  icon?: IconSource;
  component: ComponentType<unknown>;
  singleton?: boolean;
  minimized: boolean;
  maximized: boolean;
  zIndex: number;
  prevGeometry?: WindowGeometry;
  params?: unknown;
}

export interface RegistryEntryForLaunch {
  id: string;
  title: string;
  icon?: IconSource;
  singleton?: boolean;
  component: ComponentType<unknown>;
  defaultSize?: { width: number; height: number };
}

export interface WindowStore {
  windows: WindowState[];
  focusedId: string | null;
  zIndexSeq: number;

  openWindow: (entry: RegistryEntryForLaunch, params?: unknown) => string;
  findByAppId: (appId: string) => WindowState | undefined;
  focus: (id: string) => void;
  minimize: (id: string) => void;
  maximize: (id: string) => void;
  restore: (id: string) => void;
  close: (id: string) => void;
  setGeometry: (id: string, geom: Partial<WindowGeometry>) => void;
}

const DEFAULT_SIZE = { width: 640, height: 420 };
const MIN_SIZE = { width: 320, height: 220 };

let _idSeq = 1;
const nextId = () => `w-${_idSeq++}`;

function cascadeOffset(index: number): { x: number; y: number } {
  const step = 32;
  return { x: 80 + (index % 8) * step, y: 80 + (index % 8) * step };
}

export const useWindowStore = create<WindowStore>((set, get) => ({
  windows: [],
  focusedId: null,
  zIndexSeq: 1,

  openWindow(entry, params) {
    const id = nextId();
    set((state) => {
      const size = entry.defaultSize ?? DEFAULT_SIZE;
      const { x, y } = cascadeOffset(state.windows.length);
      const zIndex = state.zIndexSeq + 1;
      const win: WindowState = {
        id,
        appId: entry.id,
        title: entry.title,
        icon: entry.icon,
        component: entry.component,
        singleton: entry.singleton,
        x,
        y,
        width: Math.max(size.width, MIN_SIZE.width),
        height: Math.max(size.height, MIN_SIZE.height),
        minimized: false,
        maximized: false,
        zIndex,
        params,
      };
      return {
        windows: [...state.windows, win],
        focusedId: id,
        zIndexSeq: zIndex,
      };
    });
    return id;
  },

  findByAppId(appId) {
    return get().windows.find((w) => w.appId === appId);
  },

  focus(id) {
    set((state) => {
      const target = state.windows.find((w) => w.id === id);
      if (!target) return {};
      const zIndex = state.zIndexSeq + 1;
      return {
        focusedId: id,
        zIndexSeq: zIndex,
        windows: state.windows.map((w) =>
          w.id === id ? { ...w, zIndex, minimized: false } : w,
        ),
      };
    });
  },

  minimize(id) {
    set((state) => ({
      windows: state.windows.map((w) =>
        w.id === id ? { ...w, minimized: true } : w,
      ),
      focusedId: state.focusedId === id ? null : state.focusedId,
    }));
  },

  maximize(id) {
    set((state) => ({
      windows: state.windows.map((w) => {
        if (w.id !== id) return w;
        if (w.maximized) return w;
        return {
          ...w,
          maximized: true,
          prevGeometry: { x: w.x, y: w.y, width: w.width, height: w.height },
        };
      }),
    }));
  },

  restore(id) {
    set((state) => ({
      windows: state.windows.map((w) => {
        if (w.id !== id) return w;
        if (!w.maximized || !w.prevGeometry) return { ...w, maximized: false };
        return {
          ...w,
          ...w.prevGeometry,
          maximized: false,
          prevGeometry: undefined,
        };
      }),
    }));
  },

  close(id) {
    set((state) => ({
      windows: state.windows.filter((w) => w.id !== id),
      focusedId: state.focusedId === id ? null : state.focusedId,
    }));
  },

  setGeometry(id, geom) {
    set((state) => ({
      windows: state.windows.map((w) => {
        if (w.id !== id) return w;
        return {
          ...w,
          ...geom,
          width: Math.max(geom.width ?? w.width, MIN_SIZE.width),
          height: Math.max(geom.height ?? w.height, MIN_SIZE.height),
        };
      }),
    }));
  },
}));

export const WINDOW_MIN_SIZE = MIN_SIZE;
